<template>
  <RouterLink class="queue-card" :to="queueRoute">
    <div class="queue-card__left-content">
      <ListBulletIcon class="queue-card__icon" />
      <span class="queue-card__name">{{ queue.Name }}</span>
    </div>
    <div class="queue-card__right-content">
      <ParentTaskPlate v-if="queue.ParentTask" is-short :task="queue.ParentTask" />
      <span class="queue-card__tasks-count">{{ tasksCountText }}</span>
      <div class="queue-card__actions" v-if="isOwner || isProjectAdmin || isNamespaceAdmin">
        <SidebarMorePopover
          :position="PopoverPositionEnum.Left"
          :menu-item="queue"
          :key="queue.Name"
          @on-delete-queue="emit('onDeleteQueue', $event)"
          @on-update-queue="emit('onUpdateQueue', $event)"
          @edit-queue="openEditQueueModal"
          @toggleVisible="checkToggleVisible"
        >
          <template #target>
            <TirPmButton
              :variant="TirPmButtonVariantEnum.LinkState"
              :size="TirPmButtonSizeEnum.Regular"
              :isPressed="toggleVisible"
              @click.prevent="togglePopover"
            >
              <template #leftSlot>
                <div class="sidebar-tree-item__dots-icon">
                  <EllipsisHorizontalIcon style="width: 1.5rem" class="menu__controls-icon" />
                </div>
              </template>
            </TirPmButton>
          </template>
        </SidebarMorePopover>
      </div>
    </div>
    <EditQueueModal :queue="editQueue" @close="closeEditQueueModal" />
  </RouterLink>
</template>

<script lang="ts" setup>
import { computed, inject, PropType, watch } from 'vue';
import { QueueCardtemModel } from './models';
import { EllipsisHorizontalIcon, ListBulletIcon } from 'tir-style-system/icons/outline';
import {
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  PopoverPositionEnum,
} from 'tir-components';
import { RouteLocationRaw, RouterLink, useRouter } from 'vue-router';
import SidebarMorePopover from '../sidebar/components/sidebar-more-popover/sidebar-more-popover.vue';
import { QueueTasksModel } from '../sidebar/models';
import EditQueueModal from '../edit-queue-modal/edit-queue-modal.vue';
import { ref } from 'vue';
import { ComputedRef } from 'vue';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import { ParentTaskPlate } from '../parent-task-plate';

const router = useRouter();

const props = defineProps({
  /**Очередь */
  queue: {
    type: Object as PropType<QueueCardtemModel>,
    required: true,
  },
});

/**Владелец */
const isOwner = computed(() => CurrentUser.Roles.includes(UserRoleEnum.Owner));
/**Админ проекта */
const isProjectAdmin = inject('isAdminProject') as ComputedRef<boolean>;
/**Админ неймспейса */
const isNamespaceAdmin = inject('isAdminNamespace') as ComputedRef<boolean>;
/**Флаг для открытия очереди */
const openQueue = ref(true);

const emit = defineEmits<{
  /**Событие при удалении очереди */
  (e: 'onDeleteQueue', queueId: string): void;
  /**Событие при обновлении очереди */
  (e: 'onUpdateQueue', queue: QueueTasksModel): void;
}>();

/**Id редактируемой очереди */
const editQueue = ref<QueueTasksModel>();

/** Открытие окна для редактирования очереди  */

const toggleVisible = ref();

const checkToggleVisible = (value) => {
  toggleVisible.value = value;
};

const openEditQueueModal = () => {
  if (props.queue.isQueue()) {
    editQueue.value = props.queue;
  }
};

const togglePopover = () => {
  openQueue.value = false;
};
/**
 * * Закрыть окно редактирования очереди
 */
const closeEditQueueModal = () => (editQueue.value = undefined);

/**
 * * Получить текст с кол-во задач
 * @param count Кол-во задач
 */
const getTasksCountText = (count: number) => {
  const remainder10 = count % 10;
  const remainder100 = count % 100;

  if (remainder10 === 1 && remainder100 !== 11) {
    return `${count} задача`;
  } else if (remainder10 >= 2 && remainder10 <= 4 && !(remainder100 >= 12 && remainder100 <= 14)) {
    return `${count} задачи`;
  } else {
    return `${count} задач`;
  }
};
/**Текст с количеством задач */
const tasksCountText = computed(() => getTasksCountText(props.queue.TasksCount));
/**Путь до очереди */
const queueRoute = computed<RouteLocationRaw>(() => {
  return {
    name: 'queue',
    params: {
      queueId: props.queue.Id,
    },
  };
});
</script>

<style lang="scss" scoped>
.queue-card {
  border: 0.063rem solid var(--neutral-translucent-10, rgba(39, 45, 55, 0.102));
  padding: 1rem 0.625rem;
  border-radius: 0.5rem;
  color: var(--text-primary, #272d37);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  cursor: pointer;

  &__icon {
    flex-shrink: 0;
    width: 1.5rem;
    height: 1.5rem;
  }

  &__left-content {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    flex: 1;
  }
  &__name {
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5rem;
    color: var(--text-primary, #272d37);
  }
  &__right-content {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }
}
</style>
